<template>
  <div class="page-content">
    <div class="desktop-license-page">
      <div class="page-header">
        <h2 class="page-title">桌面客户端卡密</h2>
        <p class="page-subtitle">
          一卡一机（machine_id 64 位 hex）；客户端接口 /v1/license/activate 与 /v1/license/heartbeat。
        </p>
      </div>

      <div class="toolbar-card">
        <input
          v-model="keyword"
          type="text"
          class="search-input"
          placeholder="搜索 machine_id / code_hash..."
          @keyup.enter="loadList(1)"
        />
        <el-select v-model="filterStatus" class="status-select" clearable placeholder="状态筛选" @change="loadList(1)">
          <el-option label="未绑定" value="unbound" />
          <el-option label="已绑定" value="bound" />
          <el-option label="已过期" value="expired" />
          <el-option label="已禁用" value="disabled" />
        </el-select>
        <button class="btn secondary" @click="loadList(1)">搜索</button>
        <el-select v-model="issueDays" class="days-select">
          <el-option :value="7" label="7 天" />
          <el-option :value="30" label="30 天" />
          <el-option :value="90" label="90 天" />
          <el-option :value="365" label="365 天" />
          <el-option :value="3650" label="3650 天" />
        </el-select>
        <input
          v-model.number="issueCount"
          type="number"
          class="count-input"
          min="1"
          max="500"
          placeholder="数量"
        />
        <button class="btn primary" :disabled="issuing" @click="handleIssue">
          {{ issuing ? '生成中...' : '一键生成卡密' }}
        </button>
      </div>

      <div v-if="issuedKeys.length" class="issued-keys-card">
        <div class="issued-keys-header">
          <strong>本次生成的卡密（仅显示一次，请妥善保存）</strong>
          <button class="btn secondary" @click="copyIssuedKeys">复制全部</button>
        </div>
        <pre class="issued-keys-body">{{ issuedKeys.join('\n') }}</pre>
      </div>

      <div class="table-card" v-loading="loading">
        <div class="table-header">
          <div class="header-cell hash-col">code_hash</div>
          <div class="header-cell status-col">状态</div>
          <div class="header-cell expire-col">到期</div>
          <div class="header-cell machine-col">machine_id</div>
          <div class="header-cell time-col">最近在线</div>
          <div class="header-cell actions-col">操作</div>
        </div>

        <div v-if="rows.length === 0 && !loading" class="empty-state">
          <div class="empty-text">暂无卡密</div>
          <div class="empty-desc">点击「一键生成卡密」批量发卡</div>
        </div>

        <div v-for="row in rows" :key="row.id" class="table-row">
          <div class="cell hash-col">
            <code class="hash-text">{{ shortHash(row.code_hash) }}</code>
          </div>
          <div class="cell status-col">
            <span :class="['status-badge', row.status]">{{ statusLabel(row.status) }}</span>
          </div>
          <div class="cell expire-col">{{ formatExpire(row.expire_at) }}</div>
          <div class="cell machine-col">
            <span v-if="row.machine_id" class="machine-text">{{ shortMachine(row.machine_id) }}</span>
            <span v-else class="muted">未绑定</span>
          </div>
          <div class="cell time-col">{{ formatDateTime(row.last_seen_at) }}</div>
          <div class="cell actions-col">
            <button
              type="button"
              class="action-btn"
              :disabled="!row.machine_id"
              @click="handleUnbind(row)"
            >
              解绑
            </button>
            <button
              type="button"
              class="action-btn"
              @click="handleToggleDisabled(row)"
            >
              {{ row.disabled ? '启用' : '禁用' }}
            </button>
            <button type="button" class="action-btn danger" @click="handleDelete(row)">删除</button>
          </div>
        </div>
      </div>

      <div v-if="total > 0" class="pagination-bar">
        <span>共 {{ total }} 条，第 {{ page }} / {{ totalPages }} 页</span>
        <div class="pagination-controls">
          <button class="btn secondary" :disabled="page <= 1" @click="loadList(page - 1)">上一页</button>
          <button class="btn secondary" :disabled="page >= totalPages" @click="loadList(page + 1)">下一页</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import desktopLicenseAPI from '../api/desktopLicenses'

export default {
  name: 'DesktopLicenseManagement',
  setup() {
    const loading = ref(false)
    const issuing = ref(false)
    const rows = ref([])
    const total = ref(0)
    const page = ref(1)
    const pageSize = ref(20)
    const keyword = ref('')
    const filterStatus = ref('')
    const issueDays = ref(30)
    const issueCount = ref(10)
    const issuedKeys = ref([])

    const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))

    const formatDateTime = (value) => {
      if (!value) return '-'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '-'
      return d.toLocaleString(undefined, { hour12: false })
    }

    const formatExpire = (value) => {
      if (!value) return '-'
      const s = String(value)
      const idx = s.indexOf('T')
      return idx > 0 ? s.slice(0, idx) : s
    }

    const shortHash = (hash) => {
      if (!hash) return '-'
      return hash.length > 16 ? `${hash.slice(0, 8)}...${hash.slice(-8)}` : hash
    }

    const shortMachine = (id) => {
      if (!id) return '-'
      return id.length > 20 ? `${id.slice(0, 10)}...${id.slice(-10)}` : id
    }

    const statusLabel = (status) => {
      const map = {
        unbound: '未绑定',
        bound: '已绑定',
        expired: '已过期',
        disabled: '已禁用'
      }
      return map[status] || status || '-'
    }

    const loadList = async (targetPage = page.value) => {
      loading.value = true
      try {
        const res = await desktopLicenseAPI.list(
          targetPage,
          pageSize.value,
          keyword.value,
          filterStatus.value
        )
        if (res.code === 200 && res.data) {
          rows.value = res.data.list || []
          total.value = res.data.total || 0
          page.value = res.data.page || targetPage
        } else {
          ElMessage.error(res.message || '加载失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '加载失败')
      } finally {
        loading.value = false
      }
    }

    const copyText = async (text, successMsg) => {
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success(successMsg || '已复制到剪贴板')
      } catch (e) {
        ElMessage.error('复制失败，请检查浏览器剪贴板权限')
      }
    }

    const copyIssuedKeys = () => {
      if (!issuedKeys.value.length) return
      copyText(issuedKeys.value.join('\n'), `已复制 ${issuedKeys.value.length} 个卡密`)
    }

    const handleIssue = async () => {
      const count = Number(issueCount.value)
      if (!Number.isFinite(count) || count < 1 || count > 500) {
        ElMessage.warning('数量须在 1～500 之间')
        return
      }
      issuing.value = true
      try {
        const res = await desktopLicenseAPI.issue(issueDays.value, count)
        if (res.code === 200) {
          const keys = res.data?.keys || []
          issuedKeys.value = keys
          ElMessage.success(res.message || `已生成 ${keys.length} 个卡密`)
          await loadList(1)
          if (keys.length) {
            await copyText(keys.join('\n'), `已生成并复制 ${keys.length} 个卡密`)
          }
        } else {
          ElMessage.error(res.message || '生成失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '生成失败')
      } finally {
        issuing.value = false
      }
    }

    const handleUnbind = async (row) => {
      try {
        await ElMessageBox.confirm('确定解绑该卡密的机器吗？', '解绑确认', { type: 'warning' })
        const res = await desktopLicenseAPI.unbind(row.id)
        if (res.code === 200) {
          ElMessage.success('已解绑')
          await loadList(page.value)
        } else {
          ElMessage.error(res.message || '解绑失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error(e.response?.data?.message || e.message || '解绑失败')
        }
      }
    }

    const handleToggleDisabled = async (row) => {
      try {
        const action = row.disabled ? '启用' : '禁用'
        await ElMessageBox.confirm(`确定${action}该卡密吗？`, '确认', { type: 'warning' })
        const res = row.disabled
          ? await desktopLicenseAPI.enable(row.id)
          : await desktopLicenseAPI.disable(row.id)
        if (res.code === 200) {
          ElMessage.success(`已${action}`)
          await loadList(page.value)
        } else {
          ElMessage.error(res.message || `${action}失败`)
        }
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error(e.response?.data?.message || e.message || '操作失败')
        }
      }
    }

    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定删除该卡密吗？删除后不可恢复。', '删除确认', { type: 'warning' })
        const res = await desktopLicenseAPI.delete(row.id)
        if (res.code === 200) {
          ElMessage.success('删除成功')
          await loadList(page.value)
        } else {
          ElMessage.error(res.message || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error(e.response?.data?.message || e.message || '删除失败')
        }
      }
    }

    onMounted(() => {
      loadList(1)
    })

    return {
      loading,
      issuing,
      rows,
      total,
      page,
      totalPages,
      keyword,
      filterStatus,
      issueDays,
      issueCount,
      issuedKeys,
      formatDateTime,
      formatExpire,
      shortHash,
      shortMachine,
      statusLabel,
      loadList,
      handleIssue,
      copyIssuedKeys,
      handleUnbind,
      handleToggleDisabled,
      handleDelete
    }
  }
}
</script>

<style scoped>
.desktop-license-page {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 0 24px;
}

.page-header {
  margin-bottom: 16px;
}

.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1d1d1f;
}

.page-subtitle {
  margin: 8px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.toolbar-card {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding: 12px 14px;
  margin-bottom: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}

.search-input {
  flex: 1;
  min-width: 180px;
  height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
}

.status-select {
  width: 140px;
}

.days-select {
  width: 110px;
}

.count-input {
  width: 80px;
  height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
}

.issued-keys-card {
  margin-bottom: 12px;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  background: #fffbeb;
  padding: 12px 14px;
}

.issued-keys-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
}

.issued-keys-body {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 160px;
  overflow: auto;
}

.table-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 1.4fr 0.8fr 0.9fr 1.4fr 1.1fr 260px;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
}

.table-header {
  background: #f8f9fa;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.table-row {
  border-bottom: 1px solid #f3f4f6;
  font-size: 13px;
}

.table-row:last-child {
  border-bottom: none;
}

.hash-text,
.machine-text {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
}

.status-badge.unbound {
  background: #f3f4f6;
  color: #4b5563;
}

.status-badge.bound {
  background: #dcfce7;
  color: #166534;
}

.status-badge.expired {
  background: #fee2e2;
  color: #991b1b;
}

.status-badge.disabled {
  background: #fef3c7;
  color: #92400e;
}

.muted {
  color: #9ca3af;
}

.actions-col {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.action-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.danger {
  color: #dc2626;
  border-color: #fecaca;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
}

.empty-text {
  font-size: 15px;
  font-weight: 500;
}

.empty-desc {
  margin-top: 6px;
  font-size: 13px;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  font-size: 13px;
  color: #6b7280;
}

.pagination-controls {
  display: flex;
  gap: 8px;
}

.btn {
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn.primary {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.btn.secondary {
  background: #fff;
  color: #374151;
  border-color: #d1d5db;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
